import { useState } from "react";
import { X, Send, Film, Globe, Mail, CheckCircle, Sparkles, Building2, User, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function FestivalSubmissionModal({ isOpen, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    nombre: "",
    short_name: "",
    ciudad: "",
    pais: "",
    region: "LatAm",
    mes: "Junio",
    tipo: "Largometrajes, Cortometrajes, Documentales",
    estado: "Convocatoria Abierta",
    enlace_oficial: "",
    logo_url: "",
    banner_url: "",
    descripcion: "",
    contacto_nombre: "",
    contacto_email: "",
    contacto_cargo: "",
    notas: ""
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tipo: form.tipo.split(",").map(s => s.trim()).filter(Boolean)
      };

      const { data } = await api.post("/festival-submissions", payload);
      toast.success("¡Solicitud enviada exitosamente!");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Error al enviar la solicitud de festival.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setSubmitted(false);
    setForm({
      nombre: "",
      short_name: "",
      ciudad: "",
      pais: "",
      region: "LatAm",
      mes: "Junio",
      tipo: "Largometrajes, Cortometrajes, Documentales",
      estado: "Convocatoria Abierta",
      enlace_oficial: "",
      logo_url: "",
      banner_url: "",
      descripcion: "",
      contacto_nombre: "",
      contacto_email: "",
      contacto_cargo: "",
      notas: ""
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-2xl p-6 sm:p-8 relative shadow-2xl my-8">
        <button
          onClick={handleResetAndClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-white p-2 rounded-xl hover:bg-zinc-800 transition-colors"
        >
          <X size={20} />
        </button>

        {submitted ? (
          <div className="py-8 text-center space-y-4">
            <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto shadow-lg">
              <CheckCircle size={36} />
            </div>
            <h3 className="text-2xl font-black text-white">¡Solicitud Enviada con Éxito!</h3>
            <p className="text-zinc-300 text-sm max-w-md mx-auto leading-relaxed">
              Gracias por postular tu festival a la <strong className="text-white">Guía Mundial de Cine LGBTQ+</strong>. Nuestro equipo de administración revisará la información ingresada y, una vez aprobada, aparecerá publicada en nuestra base de datos internacional.
            </p>
            <div className="pt-4">
              <button
                onClick={handleResetAndClose}
                className="px-6 py-2.5 bg-[#E11D48] hover:bg-[#BE123C] text-white font-bold rounded-xl text-xs transition-colors shadow-lg"
              >
                Entendido / Cerrar
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-[#E11D48]/20 border border-[#E11D48]/30 rounded-xl text-[#E11D48]">
                <Film size={22} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Solicitud para Agregar Festival LGBTQ+</h3>
                <p className="text-zinc-400 text-xs">Formulario oficial de postulación para comités y equipos organizadores.</p>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 my-4 text-xs text-amber-200 flex items-start gap-2">
              <Sparkles size={16} className="text-amber-400 shrink-0 mt-0.5" />
              <span>
                <strong>Nota:</strong> Una vez enviado el formulario, tu festival quedará registrado como pendiente. El equipo de administración de La Caja LGBT lo validará y publicará en la guía global.
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs mt-4">
              {/* Festival Core Data */}
              <div className="border-b border-white/10 pb-4 space-y-3">
                <h4 className="font-bold text-zinc-300 text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <Building2 size={14} className="text-[#E11D48]" /> Información del Festival
                </h4>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Nombre Oficial del Festival *</label>
                  <input
                    required
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    placeholder="Ej: Festival Internacional de Cine LGTB de Colombia"
                    className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">Acrónimo / Nombre Corto</label>
                    <input
                      value={form.short_name}
                      onChange={(e) => setForm({ ...form, short_name: e.target.value })}
                      placeholder="Ej: FICLGTB"
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">Región *</label>
                    <select
                      value={form.region}
                      onChange={(e) => setForm({ ...form, region: e.target.value })}
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    >
                      <option value="LatAm">América Latina (LatAm)</option>
                      <option value="Europa">Europa</option>
                      <option value="Norteamérica">Norteamérica</option>
                      <option value="Asia/Oceanía">Asia / Oceanía</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">Ciudad *</label>
                    <input
                      required
                      value={form.ciudad}
                      onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
                      placeholder="Ej: Bogotá"
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">País *</label>
                    <input
                      required
                      value={form.pais}
                      onChange={(e) => setForm({ ...form, pais: e.target.value })}
                      placeholder="Ej: Colombia"
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">Mes de Celebración *</label>
                    <select
                      value={form.mes}
                      onChange={(e) => setForm({ ...form, mes: e.target.value })}
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    >
                      {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">Estado de Convocatoria</label>
                    <select
                      value={form.estado}
                      onChange={(e) => setForm({ ...form, estado: e.target.value })}
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    >
                      <option value="Convocatoria Abierta">Convocatoria Abierta</option>
                      <option value="Próxima Edición">Próxima Edición</option>
                      <option value="Cerrado">Cerrado</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">Sitio Web Oficial / Enlace</label>
                    <input
                      value={form.enlace_oficial}
                      onChange={(e) => setForm({ ...form, enlace_oficial: e.target.value })}
                      placeholder="https://misitiofestival.org"
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Categorías Aceptadas (separadas por coma)</label>
                  <input
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    placeholder="Largometrajes, Cortometrajes, Documentales, Trans/Queer, Animación"
                    className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">URL Logo (Opcional)</label>
                    <input
                      value={form.logo_url}
                      onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">URL Cartel / Banner (Opcional)</label>
                    <input
                      value={form.banner_url}
                      onChange={(e) => setForm({ ...form, banner_url: e.target.value })}
                      placeholder="https://images.unsplash.com/..."
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Descripción / Propuesta del Festival</label>
                  <textarea
                    rows={3}
                    value={form.descripcion}
                    onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                    placeholder="Misión del festival, enfoque cinematográfico queer, años de trayectoria..."
                    className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>
              </div>

              {/* Organizer Contact Info */}
              <div className="space-y-3 pt-2">
                <h4 className="font-bold text-zinc-300 text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <User size={14} className="text-[#E11D48]" /> Contacto del Equipo Organizador
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">Nombre Completo del Contacto</label>
                    <input
                      value={form.contacto_nombre}
                      onChange={(e) => setForm({ ...form, contacto_nombre: e.target.value })}
                      placeholder="Ej: Alex Martínez"
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 font-semibold mb-1">Correo Electrónico de Contacto *</label>
                    <input
                      required
                      type="email"
                      value={form.contacto_email}
                      onChange={(e) => setForm({ ...form, contacto_email: e.target.value })}
                      placeholder="contacto@festival.org"
                      className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Cargo en la Organización</label>
                  <input
                    value={form.contacto_cargo}
                    onChange={(e) => setForm({ ...form, contacto_cargo: e.target.value })}
                    placeholder="Ej: Director/a de Programación / Productor/a"
                    className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Notas o Mensaje Adicional</label>
                  <input
                    value={form.notas}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                    placeholder="Detalles sobre fechas exactas de inscripción, costo de bases, etc."
                    className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={handleResetAndClose}
                  className="px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-semibold rounded-xl text-xs transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-[#E11D48] hover:bg-[#BE123C] text-white font-bold rounded-xl text-xs transition-all flex items-center gap-2 shadow-lg"
                >
                  <Send size={14} />
                  {submitting ? "Enviando Solicitud..." : "Enviar Solicitud de Festival"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
